import { useState } from "react";
import { cleanUsername, MAX_IMAGE_BYTES, menuUrl } from "../../menu.js";
import { BUSINESS_LIMITS, PROVINCIAS, validateBusiness } from "../../businessValidation.js";
import { notifyError } from "./useBusiness";
import { openStatus } from "../../hours.js";

const max = (key) => BUSINESS_LIMITS[key].max;

// Debajo del horario: cómo lo va a leer el cartel "Abierto ahora" del menú.
const HoursHint = ({ horario, horarioSec }) => {
    if (!horario.trim() && !horarioSec.trim()) return null;
    const status = openStatus({ horario, horarioSec });
    if (!status) {
        return <p className="lp-field-hint lp-hours-hint">No entendemos este horario, así que el menú no va a mostrar si estás abierto. Escribilo con días y horas, por ejemplo "Lunes a viernes de 8 a 20" y "Sábados de 9 a 13".</p>;
    }
    return <p className="lp-field-hint lp-hours-hint">Tu menú muestra si estás abierto. Ahora diría: <strong>{status.label}{status.detail ? " · " + status.detail : ""}</strong>.</p>;
}

const fieldsFrom = (business) => ({
    nombreEmpresa: business?.nombreEmpresa || "",
    descripcionEmpresa: business?.descripcionEmpresa || "",
    calle: business?.calle || "",
    altura: business?.altura || "",
    ciudad: business?.ciudad || "",
    provincia: business?.provincia || "",
    telefono: business?.telefono || "",
    horario: business?.horario || "",
    horarioSec: business?.horarioSec || ""
});

// Datos del negocio. Sin `business` es el alta (pide usuario y logo);
// con `business` edita los datos, y el usuario queda fijo porque es el link del menú y del QR.
const BusinessForm = ({ business, onSubmit, onCancel, submitLabel = "Guardar" }) => {

    const isNew = !business;
    const [fields, setFields] = useState(() => fieldsFrom(business));
    const [usuarioEmpresa, setUsuarioEmpresa] = useState("");
    const [logo, setLogo] = useState(null);
    const [preview, setPreview] = useState(business?.logo || "");
    const [saving, setSaving] = useState(false);
    const [errors, setErrors] = useState({});

    // Al corregir un campo se le saca el error.
    const clear = (name) => setErrors((e) => {
        if (!e[name]) return e;
        const { [name]: _, ...rest } = e;
        return rest;
    });
    const set = (name) => (e) => {
        setFields((f) => ({ ...f, [name]: e.target.value }));
        clear(name);
    }

    // Props de cada campo para marcarlo y leer su error con lector de pantalla.
    const invalid = (name) => errors[name] ? { "aria-invalid": true, "aria-describedby": "bf-error-" + name } : {};
    const errorOf = (name) => errors[name] && <span className="lp-field-error" id={"bf-error-" + name} role="alert">{errors[name]}</span>;

    const handleSubmit = async (e) => {
        e.preventDefault();
        const result = validateBusiness(isNew ? { ...fields, usuarioEmpresa } : fields, { isNew });
        if (isNew && !logo) {
            result.errors = { ...result.errors, logo: "Elegí el logo de tu negocio." };
        }
        if (result.errors) {
            setErrors(result.errors);
            notifyError("Revisá los datos marcados en rojo.");
            const first = e.target.querySelector("[aria-invalid='true']");
            if (first) first.focus();
            return;
        }
        setErrors({});
        // Lo que se guarda es lo limpio, y el formulario lo muestra así.
        setFields((f) => ({ ...f, ...result.data }));
        setSaving(true);
        await onSubmit(result.data, logo);
        setSaving(false);
    }

  return (
    <form className="lp-panel-form" onSubmit={handleSubmit} noValidate>

        <div className="lp-field">
            <label htmlFor="bf-nombre">Nombre del negocio</label>
            <input id="bf-nombre" type="text" required maxLength={max("nombreEmpresa")} autoComplete="organization" value={fields.nombreEmpresa} onChange={set("nombreEmpresa")} {...invalid("nombreEmpresa")}/>
            {errorOf("nombreEmpresa")}
        </div>

        {isNew &&
            <div className="lp-field">
                <label htmlFor="bf-usuario">Nombre de usuario</label>
                <input id="bf-usuario" type="text" required autoCapitalize="none" autoComplete="off" spellCheck="false" minLength={BUSINESS_LIMITS.usuarioEmpresa.min} maxLength={max("usuarioEmpresa")} value={usuarioEmpresa} onChange={(e) => { setUsuarioEmpresa(cleanUsername(e.target.value)); clear("usuarioEmpresa"); }} {...invalid("usuarioEmpresa")}/>
                {errorOf("usuarioEmpresa")}
                <span className="lp-field-hint">Solo letras minúsculas, números y guiones. Tu menú va a quedar en {menuUrl(usuarioEmpresa || "tu-negocio")}. Después no se puede cambiar.</span>
            </div>
        }

        <div className="lp-field">
            <span className="lp-panel-label">Logo</span>
            <div className="lp-panel-logo-pick">
                {preview ? <img src={preview} alt="Logo del negocio"/> : <span className="lp-panel-logo-empty" aria-hidden="true"></span>}
                <label className="lp-btn lp-btn-secondary lp-panel-btn-sm" htmlFor="bf-logo">{preview ? "Cambiar logo" : "Elegir logo"}</label>
                <input id="bf-logo" className="lp-panel-file" type="file" accept="image/*" required={isNew} onChange={(e) => {
                    const file = e.target.files[0];
                    if (!file) return;
                    if (!file.type.startsWith("image/")) {
                        e.target.value = "";
                        notifyError("El logo tiene que ser una imagen (JPG, PNG o WebP).");
                        return;
                    }
                    if (file.size > MAX_IMAGE_BYTES) {
                        e.target.value = "";
                        notifyError("El logo pesa más de 5 MB. Probá con uno más liviano.");
                        return;
                    }
                    setLogo(file);
                    setPreview(URL.createObjectURL(file));
                    clear("logo");
                }} {...invalid("logo")}/>
            </div>
            {errorOf("logo")}
        </div>

        <div className="lp-field">
            <label htmlFor="bf-desc">Descripción</label>
            <textarea id="bf-desc" rows="3" maxLength={max("descripcionEmpresa")} required value={fields.descripcionEmpresa} onChange={set("descripcionEmpresa")} {...invalid("descripcionEmpresa")}></textarea>
            {errorOf("descripcionEmpresa")}
            <span className="lp-field-hint">Una o dos frases. Se ve arriba de tu menú.</span>
        </div>

        <fieldset className="lp-panel-fieldset">
            <legend>Dirección</legend>
            <div className="lp-panel-grid-2">
                <div className="lp-field">
                    <label htmlFor="bf-calle">Calle</label>
                    <input id="bf-calle" type="text" required maxLength={max("calle")} value={fields.calle} onChange={set("calle")} {...invalid("calle")}/>
                    {errorOf("calle")}
                </div>
                <div className="lp-field">
                    <label htmlFor="bf-altura">Número</label>
                    <input id="bf-altura" type="text" required maxLength={max("altura")} placeholder="1234 o S/N" value={fields.altura} onChange={set("altura")} {...invalid("altura")}/>
                    {errorOf("altura")}
                </div>
                <div className="lp-field">
                    <label htmlFor="bf-ciudad">Ciudad</label>
                    <input id="bf-ciudad" type="text" required maxLength={max("ciudad")} value={fields.ciudad} onChange={set("ciudad")} {...invalid("ciudad")}/>
                    {errorOf("ciudad")}
                </div>
                <div className="lp-field">
                    <label htmlFor="bf-provincia">Provincia</label>
                    <input id="bf-provincia" type="text" required maxLength={max("provincia")} list="bf-provincias" value={fields.provincia} onChange={set("provincia")} {...invalid("provincia")}/>
                    <datalist id="bf-provincias">
                        {PROVINCIAS.map((p) => <option key={p} value={p}></option>)}
                    </datalist>
                    {errorOf("provincia")}
                </div>
            </div>
        </fieldset>

        <div className="lp-field">
            <label htmlFor="bf-telefono">WhatsApp</label>
            <input id="bf-telefono" type="tel" required maxLength={25} autoComplete="tel" placeholder="341 555-1234" value={fields.telefono} onChange={set("telefono")} {...invalid("telefono")}/>
            {errorOf("telefono")}
            <span className="lp-field-hint">Código de área y número, sin 0 ni 15. Si es de otro país, con su código (por ejemplo +598...).</span>
        </div>

        <div className="lp-panel-grid-2">
            <div className="lp-field">
                <label htmlFor="bf-horario">Horario principal</label>
                <input id="bf-horario" type="text" required maxLength={max("horario")} placeholder="Lunes a viernes de 8 a 17" value={fields.horario} onChange={set("horario")} {...invalid("horario")}/>
                {errorOf("horario")}
            </div>
            <div className="lp-field">
                <label htmlFor="bf-horario2">Otro horario (opcional)</label>
                <input id="bf-horario2" type="text" maxLength={max("horarioSec")} placeholder="Sábados de 9 a 13" value={fields.horarioSec} onChange={set("horarioSec")} {...invalid("horarioSec")}/>
                {errorOf("horarioSec")}
            </div>
        </div>
        <HoursHint horario={fields.horario} horarioSec={fields.horarioSec}></HoursHint>

        <div className="lp-panel-actions">
            <button className="lp-btn lp-btn-primary" type="submit" disabled={saving}>{saving ? "Guardando..." : submitLabel}</button>
            {onCancel && <button className="lp-btn lp-btn-secondary" type="button" onClick={onCancel}>Cancelar</button>}
        </div>
    </form>
  )
}

export default BusinessForm
